import React, { useState, useCallback } from 'react'
import { Modal, Button, Table, Alert, ProgressBar, Badge } from 'react-bootstrap'
import { useMutation } from '@apollo/client'
import { parseCSV, ParsedContact, contactsToGraphQLInput } from '../../../utils/csvParser'
import { IMPORT_CAMPAIGN_CONTACTS } from '../../../gql/mutations/campaigns'
import { PhoneProviderActions } from '../../../components/telephony/PhoneProviderActions'
import { useIntl, FormattedMessage } from 'react-intl'
import {
  CAMPAIGN_MESSAGES as M,
  CSV_COLUMNS,
  CSV_EXAMPLE,
  EM_DASH,
  ROW_NUMBER_HEADER,
  formatPercent,
} from '../campaignMessages'
import './CSVImportModal.css'

interface CSVImportModalProps {
  show: boolean
  onHide: () => void
  onImportSuccess: (importedContactIds: string[]) => void
}

type ImportStep = 'upload' | 'preview' | 'importing' | 'complete'

export const CSVImportModal: React.FC<CSVImportModalProps> = ({
  show,
  onHide,
  onImportSuccess,
}) => {
  const intl = useIntl()
  const code = (chunks: React.ReactNode) => <code>{chunks}</code>
  const [step, setStep] = useState<ImportStep>('upload')
  const [dragActive, setDragActive] = useState(false)
  const [parsedData, setParsedData] = useState<ParsedContact[]>([])
  const [headers, setHeaders] = useState<string[]>([])
  const [stats, setStats] = useState({ valid: 0, errors: 0, total: 0 })
  const [importProgress, setImportProgress] = useState(0)
  const [importedIds, setImportedIds] = useState<string[]>([])
  const [importStats, setImportStats] = useState({ added: 0, tagged: 0 })

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0])
    }
  }, [])

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0])
    }
  }

  const handleFile = (file: File) => {
    if (!file.name.endsWith('.csv')) {
      alert(intl.formatMessage(M.importAlertNotCsv))
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string
        const result = parseCSV(text)

        setParsedData(result.contacts)
        setHeaders(result.headers)
        setStats({
          valid: result.validCount,
          errors: result.errorCount,
          total: result.totalRows,
        })
        setStep('preview')
      } catch (error) {
        alert(
          intl.formatMessage(M.importAlertParse, {
            message: error instanceof Error ? error.message : intl.formatMessage(M.unknownError),
          })
        )
      }
    }
    reader.readAsText(file)
  }

  const [importCampaignContacts] = useMutation(IMPORT_CAMPAIGN_CONTACTS)

  const handleImport = async () => {
    setStep('importing')
    setImportProgress(20)
    setImportedIds([])
    setImportStats({ added: 0, tagged: 0 })

    try {
      // Filter only valid contacts
      const validContacts = parsedData.filter(c => c.errors.length === 0)
      const inputs = contactsToGraphQLInput(validContacts)
      
      if (inputs.length === 0) {
        alert(intl.formatMessage(M.importAlertNoValid))
        setStep('preview')
        return
      }

      setImportProgress(50)

      const response = await importCampaignContacts({
        variables: { contacts: inputs }
      })

      setImportProgress(100)

      const data = response.data?.importCampaignContacts
      if (data?.ok) {
        setImportedIds(data.importedIds || [])
        setImportStats({
          added: data.addedCount || 0,
          tagged: data.taggedExistingCount || 0
        })
        setStep('complete')
      } else {
        alert(intl.formatMessage(M.importAlertFailed))
        setStep('preview')
      }

    } catch (error) {
      alert(
        intl.formatMessage(M.importAlertError, {
          message: error instanceof Error ? error.message : intl.formatMessage(M.unknownError),
        })
      )
      setStep('preview')
    }
  }

  const handleComplete = () => {
    onImportSuccess(importedIds)
    handleClose()
  }

  const handleClose = () => {
    setStep('upload')
    setParsedData([])
    setHeaders([])
    setStats({ valid: 0, errors: 0, total: 0 })
    setImportProgress(0)
    setImportedIds([])
    onHide()
  }

  const downloadTemplate = () => {
    const headers = ['first_name', 'last_name', 'email', 'phone']
    const example = ['John,Doe,john@example.com,+1234567890']
    const csvContent = [headers.join(','), ...example].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', 'contacts_template.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <Modal show={show} onHide={handleClose} size='xl' centered>
      <Modal.Header closeButton>
        <Modal.Title><FormattedMessage {...M.importTitle} /></Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div className='csv-import-upload'>
            <div
              className={`csv-dropzone ${dragActive ? 'active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <i className='bi bi-cloud-upload fs-1 text-primary mb-3'></i>
              <h5><FormattedMessage {...M.importDropzone} /></h5>
              <p className='text-muted'><FormattedMessage {...M.importOr} /></p>
              <label htmlFor='csv-file-input' className='btn btn-primary'>
                <i className='bi bi-folder2-open me-2'></i>
                <FormattedMessage {...M.importChooseFile} />
              </label>
              <input
                id='csv-file-input'
                type='file'
                accept='.csv'
                onChange={handleFileInput}
                style={{ display: 'none' }}
              />
            </div>

            <Alert variant='info' className='mt-4'>
              <div className='d-flex justify-content-between align-items-start'>
                <div>
                  <Alert.Heading className='h6'>
                    <FormattedMessage {...M.importFormatTitle} />
                  </Alert.Heading>
                  <ul className='mb-0'>
                    <li>
                      <FormattedMessage
                        {...M.importFormatColumns}
                        values={{columns: <code>{CSV_COLUMNS}</code>}}
                      />
                    </li>
                    <li><FormattedMessage {...M.importFormatEither} /></li>
                    <li><FormattedMessage {...M.importFormatHeaders} /></li>
                    <li>
                      <FormattedMessage
                        {...M.importFormatExample}
                        values={{example: <code>{CSV_EXAMPLE}</code>}}
                      />
                    </li>
                  </ul>
                </div>
                <Button
                  variant='outline-info'
                  size='sm'
                  onClick={downloadTemplate}
                  className='bg-white text-info border-info'
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <i className='bi bi-download me-2'></i>
                  <FormattedMessage {...M.importDownloadTemplate} />
                </Button>
              </div>
            </Alert>
          </div>
        )}

        {/* Step 2: Preview */}
        {step === 'preview' && (
          <div className='csv-import-preview'>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <div>
                <h6 className='mb-1'><FormattedMessage {...M.importPreviewTitle} /></h6>
                <div className='d-flex gap-3'>
                  <Badge bg='success'>
                    <i className='bi bi-check-circle me-1'></i>
                    <FormattedMessage {...M.importValidCount} values={{count: stats.valid}} />
                  </Badge>
                  {stats.errors > 0 && (
                    <Badge bg='danger'>
                      <i className='bi bi-exclamation-circle me-1'></i>
                      <FormattedMessage {...M.importErrorCount} values={{count: stats.errors}} />
                    </Badge>
                  )}
                  <Badge bg='secondary'>
                    <FormattedMessage {...M.importTotalCount} values={{count: stats.total}} />
                  </Badge>
                </div>
              </div>
            </div>

            <div className='table-container'>
              <Table striped bordered hover size='sm'>
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>{ROW_NUMBER_HEADER}</th>
                    <th><FormattedMessage {...M.firstName} /></th>
                    <th><FormattedMessage {...M.lastName} /></th>
                    <th><FormattedMessage {...M.email} /></th>
                    <th><FormattedMessage {...M.phone} /></th>
                    <th><FormattedMessage {...M.status} /></th>
                  </tr>
                </thead>
                <tbody>
                  {parsedData.slice(0, 50).map((contact, index) => (
                    <tr key={index} className={contact.errors.length > 0 ? 'table-danger' : ''}>
                      <td>{contact.rowNumber}</td>
                      <td>{contact.firstName}</td>
                      <td>{contact.lastName}</td>
                      <td>{contact.email}</td>
                      <td>
                        <PhoneProviderActions phone={contact.phone} emptyText={EM_DASH} />
                      </td>
                      <td>
                        {contact.errors.length > 0 ? (
                          <span className='text-danger small'>
                            <i className='bi bi-exclamation-triangle me-1'></i>
                            {contact.errors[0]}
                          </span>
                        ) : (
                          <span className='text-success small'>
                            <i className='bi bi-check-circle me-1'></i>
                            <FormattedMessage {...M.importValid} />
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {parsedData.length > 50 && (
                <p className='text-muted text-center small'>
                  <FormattedMessage
                    {...M.importShowingFirst}
                    values={{shown: 50, total: parsedData.length}}
                  />
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Importing */}
        {step === 'importing' && (
          <div className='csv-import-importing text-center py-5'>
            <div className='spinner-border text-primary mb-3' style={{ width: '3rem', height: '3rem' }}>
              <span className='visually-hidden'>{intl.formatMessage(M.loading)}</span>
            </div>
            <h5><FormattedMessage {...M.importInProgress} /></h5>
            <p className='text-muted'><FormattedMessage {...M.importPleaseWait} /></p>
            <ProgressBar animated now={importProgress} className='mt-3' style={{ maxWidth: '400px', margin: '0 auto' }} />
            <p className='small text-muted mt-2'>{formatPercent(importProgress)}</p>
          </div>
        )}

        {/* Step 4: Complete */}
        {step === 'complete' && (
          <div className='csv-import-complete text-center py-5'>
            <i className='bi bi-check-circle-fill text-success' style={{ fontSize: '4rem' }}></i>
            <h4 className='mt-3'><FormattedMessage {...M.importSuccessTitle} /></h4>
            <p className='text-muted mb-4'>
              <FormattedMessage {...M.importProcessed} values={{count: importedIds.length}} />
            </p>
            <div className='d-flex justify-content-center gap-4 text-center'>
              <div className='p-3 border rounded bg-light min-w-150px'>
                <h2 className='text-primary fw-bold mb-1'>{importStats.added}</h2>
                <div className='small text-muted text-uppercase fw-semibold'>
                  <FormattedMessage {...M.importNewCreated} />
                </div>
              </div>
              <div className='p-3 border rounded bg-light min-w-150px'>
                <h2 className='text-warning fw-bold mb-1'>{importStats.tagged}</h2>
                <div className='small text-muted text-uppercase fw-semibold'>
                  <FormattedMessage {...M.importExistingTagged} />
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {step === 'upload' && (
          <Button variant='secondary' onClick={handleClose}>
            <FormattedMessage {...M.cancel} />
          </Button>
        )}
        {step === 'preview' && (
          <>
            <Button variant='secondary' onClick={() => setStep('upload')}>
              <i className='bi bi-arrow-left me-2'></i>
              <FormattedMessage {...M.back} />
            </Button>
            <Button
              variant='primary'
              onClick={handleImport}
              disabled={stats.valid === 0}
            >
              <i className='bi bi-upload me-2'></i>
              <FormattedMessage {...M.importAction} values={{count: stats.valid}} />
            </Button>
          </>
        )}
        {step === 'complete' && (
          <Button variant='success' onClick={handleComplete}>
            <i className='bi bi-check2 me-2'></i>
            <FormattedMessage {...M.importDone} />
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
